import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Chip,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Settings, Close } from '@mui/icons-material';
import { PaymentFrequency } from '../../__generated__/types';
import { PLAN_CONFIGURATIONS, PlanConfiguration } from './plan-configuration';
import { useHighLevelContext } from '../../hooks/HighLevelContext';

interface PlanAdminPanelProps {
  open: boolean;
  onClose: () => void;
  onConfigurationChange?: (configurations: PlanConfiguration[]) => void;
}

export const PlanAdminPanel: React.FC<PlanAdminPanelProps> = ({
  open,
  onClose,
  onConfigurationChange,
}) => {
  const [configurations, setConfigurations] = useState<PlanConfiguration[]>([...PLAN_CONFIGURATIONS]);
  const { clearPlanCache } = useHighLevelContext();

  const getFrequencyLabel = (frequency: PaymentFrequency): string => {
    switch (frequency) {
      case PaymentFrequency.Monthly:
        return 'Monthly';
      case PaymentFrequency.Annually:
        return 'Annual';
      case PaymentFrequency.BiAnnually:
        return 'Bi-Annual';
      default:
        return 'Unknown';
    }
  };

  const getTierColor = (tier: string): string => {
    switch (tier) {
      case 'Home':
        return '#4CAF50';
      case 'Design':
        return '#2196F3';
      case 'Design+':
        return '#9C27B0';
      case 'Basic':
        return '#6B7280';
      case 'Guild':
        return '#2563EB';
      case 'Guild+':
        return '#D22268';
      default:
        return '#757575';
    }
  };

  const handleToggleEnabled = (index: number) => {
    const newConfigurations = [...configurations];
    newConfigurations[index] = {
      ...newConfigurations[index],
      enabled: !newConfigurations[index].enabled,
    };
    setConfigurations(newConfigurations);
  };

  const handleToggleComingSoon = (index: number) => {
    const newConfigurations = [...configurations];
    newConfigurations[index] = {
      ...newConfigurations[index],
      comingSoon: !newConfigurations[index].comingSoon,
    };
    setConfigurations(newConfigurations);
  };

  const handleSave = () => {
    onConfigurationChange?.(configurations);
    onClose();
  };

  const handleReset = () => {
    setConfigurations([...PLAN_CONFIGURATIONS]);
  };

  const handleClearCache = () => {
    clearPlanCache();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Settings />
            <Typography variant="h6">Plan Configuration</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure which membership plans are displayed on the pricing page. 
          Disabled plans will show as placeholders if placeholders are enabled.
        </Typography>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Tier</strong></TableCell>
                <TableCell><strong>Frequency</strong></TableCell>
                <TableCell align="center"><strong>Enabled</strong></TableCell>
                <TableCell align="center"><strong>Coming Soon</strong></TableCell>
                <TableCell align="center"><strong>Priority</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configurations.map((config, index) => (
                <TableRow key={`${config.tier}-${config.frequency}`}>
                  <TableCell>
                    <Chip
                      label={config.tier}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                        height: { xs: 24, md: 28 },
                        py: 0.5,
                        px: 0.5,
                        minWidth: 'auto',
                        width: 'fit-content',
                        borderRadius: 50,
                        fontWeight: 500,
                        backgroundColor: 'transparent',
                        color: getTierColor(config.tier),
                        borderColor: getTierColor(config.tier),
                        borderWidth: 1,
                      }}
                    />
                  </TableCell>
                  <TableCell>{getFrequencyLabel(config.frequency)}</TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={config.enabled}
                      onChange={() => handleToggleEnabled(index)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={config.comingSoon || false}
                      onChange={() => handleToggleComingSoon(index)}
                      disabled={config.enabled}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{config.priority}</Typography>
                  </TableCell>
                  <TableCell>
                    {config.enabled ? (
                      <Chip 
                        label="Active" 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          fontSize: { xs: '0.75rem', md: '0.875rem' },
                          height: { xs: 24, md: 28 },
                          py: 0.5,
                          px: 0.5,
                          minWidth: 'auto',
                          width: 'fit-content',
                          borderRadius: 50,
                          fontWeight: 500,
                          backgroundColor: 'transparent',
                          color: '#4CAF50',
                          borderColor: '#4CAF50',
                          borderWidth: 1,
                        }} 
                      />
                    ) : config.comingSoon ? (
                      <Chip 
                        label="Coming Soon" 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          fontSize: { xs: '0.75rem', md: '0.875rem' },
                          height: { xs: 24, md: 28 },
                          py: 0.5,
                          px: 0.5,
                          minWidth: 'auto',
                          width: 'fit-content',
                          borderRadius: 50,
                          fontWeight: 500,
                          backgroundColor: 'transparent',
                          color: '#FF9800',
                          borderColor: '#FF9800',
                          borderWidth: 1,
                        }} 
                      />
                    ) : (
                      <Chip 
                        label="Placeholder" 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          fontSize: { xs: '0.75rem', md: '0.875rem' },
                          height: { xs: 24, md: 28 },
                          py: 0.5,
                          px: 0.5,
                          minWidth: 'auto',
                          width: 'fit-content',
                          borderRadius: 50,
                          fontWeight: 500,
                          backgroundColor: 'transparent',
                          color: '#9E9E9E',
                          borderColor: '#9E9E9E',
                          borderWidth: 1,
                        }} 
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box mt={2}>
        <Typography variant="caption" color="text.secondary">
          <strong>Note:</strong> Changes are temporary and will reset on page reload. 
          For permanent changes, update the plan-configuration.ts file.
          Use "Clear Plan Cache" to force a fresh fetch from HighLevel.
        </Typography>
      </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClearCache} color="inherit">
          Clear Plan Cache
        </Button>
        <Button onClick={handleReset} color="secondary">
          Reset to Default
        </Button>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Apply Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Floating Admin Button Component
interface AdminButtonProps {
  onClick: () => void;
}

export const FloatingAdminButton: React.FC<AdminButtonProps> = ({ onClick }) => {
  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <Tooltip title="Configure Plans" placement="left">
      <IconButton
        onClick={onClick}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          backgroundColor: 'primary.main',
          color: 'white',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
          zIndex: 1000,
        }}
      >
        <Settings />
      </IconButton>
    </Tooltip>
  );
};
